import { Popconfirm, Space, Switch } from 'antd';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { useMemo, useState, type VFC } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { notifyError, notifySuccess } from 'utils/notifications';
import { formatQuery, QueryBuilder } from 'components/QueryBuilder';
import { Button, Card, PageHeader } from 'components';
import { useUpdateHardware } from 'api/hooks';
import type { RuleGroupType } from 'react-querybuilder';
import { useManufacturers } from 'api/hooks/manufacturers/useManufacturers';
import { useMeterModels } from 'api/hooks/meter-models/useMeterModels';
import { GET_HARDWARE_DATA, type HardwareData } from '../../queries/getHardwareData';
import { parseQuery } from './utils';
import { usePredefinedQueryFromMeter } from './hooks/usePredefinedQueryFromMeter';
import { GET_HARDWARES_QUERY, type GetHardwaresQueryResult } from './queries/getHardwares';
import { GET_METER_ADDONS_QUERY, type GetMeterAddonsQueryResult } from './queries/getAddons';
import { getFields } from './fields';

interface QueryBuilderPageRouteState {
  inventoryMeterId?: number;
}

export const QueryBuilderPage: VFC = () => {
  const history = useHistory<QueryBuilderPageRouteState>();
  const { params } = useRouteMatch<{ id?: string }>();

  const [query, setQuery] = useState<RuleGroupType>(parseQuery());

  const [showQuery, setShowQuery] = useState(false);

  const { mutateAsync: updateHardware, isLoading: isMutating } = useUpdateHardware(() => {
    notifySuccess({
      message: `Hardware query saved.`,
    });
  });
  const { data: manufacturers, isLoading: isLoadingManufacturers } = useManufacturers({
    limit: -1,
    start: 0,
  });
  const { data: meterModels, isLoading: isLoadingMeterModels } = useMeterModels({ limit: -1, start: 0 });
  const { data: { meterAddons: { data: meterAddons = [] } = {} } = {} } =
    useStrapiQuery<GetMeterAddonsQueryResult>(GET_METER_ADDONS_QUERY);
  const { loading: isLoadingHardwaresForSelect } = useStrapiQuery<GetHardwaresQueryResult>(GET_HARDWARES_QUERY);

  const navigateBack = () => {
    history.goBack();
  };

  const inventoryMeterId = history.location.state?.inventoryMeterId;

  usePredefinedQueryFromMeter(inventoryMeterId, setQuery);

  const {
    data: response,
    loading: isLoading,
    refetch: refetchHardware,
  } = useStrapiQuery<HardwareData, { id: number }>(GET_HARDWARE_DATA, {
    variables: {
      id: Number(params.id),
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted(data) {
      const fetchedQuery = parseQuery(data.hardware.data);

      if (inventoryMeterId === undefined) {
        setQuery(fetchedQuery);
      }
    },
    onError(error) {
      notifyError(error, {
        message: `Action failed. Please try again.`,
      });

      navigateBack();
    },
  });

  const handleSave = async () => {
    if (!response?.hardware.data.id) return;

    await updateHardware({
      id: response.hardware.data.id,
      values: {
        query: JSON.parse(formatQuery(query, 'json')) as Record<string, unknown>,
      },
    });

    refetchHardware();
  };

  const handleResetButtonClick = () => {
    const fetchedQuery = parseQuery(response?.hardware.data);
    setQuery(fetchedQuery);
  };

  const handleBack = () => {
    navigateBack();
  };

  const fields = useMemo(
    () => getFields(manufacturers?.data, meterModels?.data, meterAddons, response?.hardware?.data?.category),
    [response, manufacturers, meterModels, meterAddons],
  );

  return (
    <Space className="w-100" direction="vertical">
      <PageHeader ghost={false} title={response?.hardware?.data?.name} onBack={handleBack} />
      <Card
        extra={
          <Space>
            <Switch
              defaultChecked
              checked={showQuery}
              checkedChildren="Hide query"
              unCheckedChildren="Show query"
              onChange={() => setShowQuery(!showQuery)}
            />
            <Popconfirm
              cancelText="Cancel"
              okText="Reset"
              title={
                <Space>
                  <span>Are you sure you want to reset all unsaved changes?</span>
                </Space>
              }
              zIndex={99999}
              onConfirm={handleResetButtonClick}
            >
              <Button loading={isMutating}>Reset</Button>
            </Popconfirm>
            <Button loading={isMutating} type="primary" onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
        loading={isLoading || isLoadingManufacturers || isLoadingMeterModels || isLoadingHardwaresForSelect}
      >
        <QueryBuilder
          listsAsArrays
          showCloneButtons
          controlClassnames={{ queryBuilder: 'queryBuilder-branches' }}
          fields={fields}
          query={query}
          onQueryChange={newQuery => setQuery({ ...query, ...newQuery })}
        />
      </Card>
      {showQuery && (
        <Card title="Query">
          <pre>
            <code>{formatQuery(query, 'json')}</code>
          </pre>
        </Card>
      )}
    </Space>
  );
};
